/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Button from "@mui/material/Button";
import SignInIcon from "@mui/icons-material/Input";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { useForm } from "react-hook-form";

import loginClasses from "../Login/Login.module.css";
import ReCaptcha from "../../features/auth/components/ReCaptcha";
import LocalLoader from "../../components/Loaders/Local";
import {
  useCaptchaConfigMutation,
  useUnlockAccountMutation,
} from "../../api/auth.api.slice";
import { showNotification } from "../../features/core/slices/notification.slice";
import { isSSOEnabled, notifyError } from "../../utils/helpers";

function UnlockAccountPage() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [captchaConfigData, setCaptchaConfigData] = useState({});

  const [captchaConfig] = useCaptchaConfigMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({
    mode: "onBlur",
  });

  const [respMsg, setRespMsg] = useState("");
  const [errorCode, setErrorCode] = useState(null);
  const [unlockAccount, { isLoading }] = useUnlockAccountMutation();

  useEffect(() => {
    const getCaptchaConfig = async () => {
      try {
        const { data } = await captchaConfig();
        if (data) {
          setCaptchaConfigData(data);
        }
      } catch (err) {
        notifyError(dispatch, showNotification, err);
      }
    };
    if (!isSSOEnabled) getCaptchaConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = async (data) => {
    const payload = {
      captchaToken: data.captchaToken,
      body: { username: data.email },
    };
    setRespMsg("");
    setErrorCode(null);

    try {
      const res = await unlockAccount(payload).unwrap();
      if (res && res.message) setRespMsg(res?.message);
      if (res?.result?.err?.code) setErrorCode(res.result.err.code);
    } catch (error) {
      notifyError(dispatch, showNotification, error);
    }
  };

  const error = errorCode === 401 || errorCode === 403;

  return (
    <div>
      <Grid
        container
        direction="row"
        className={loginClasses.loginGrid}
        sx={{ justifyContent: "center", alignItems: "center" }}
      >
        <Grid
          sx={{ my: "1rem", position: "relative" }}
          size={{ xs: 11, sm: 8, md: 6 }}
        >
          <Card raised>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader
                title="Unlock Account"
                slotProps={{ title: { component: "h1" } }}
                className={loginClasses.cardHeader}
              >
                Unlock Account
              </CardHeader>
              <CardContent>
                <TextField
                  label="Email"
                  id="email"
                  name="email"
                  required
                  type="text"
                  margin="normal"
                  variant="standard"
                  autoComplete="email"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Invalid email address",
                    },
                    maxLength: {
                      value: 100,
                      message: "Email must not exceed 100 characters",
                    },
                  })}
                  fullWidth
                  error={errors?.email !== undefined}
                  helperText={errors?.email ? errors.email.message : ""}
                />
              </CardContent>
              <CardActions
                disableSpacing
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {captchaConfigData?.captchaUnlockAccount && !respMsg && (
                  <Grid
                    container
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      mb: "0.5",
                    }}
                  >
                    <ReCaptcha
                      name="captchaToken"
                      control={control}
                      theme={theme.palette.mode}
                      error={errors?.captchaToken !== undefined}
                    />
                  </Grid>
                )}
                <Grid
                  container
                  sx={{
                    justifyContent: "center",
                    [theme.breakpoints.down("md")]: {
                      flexDirection: "column-reverse",
                    },
                    "& .MuiButton-root": {
                      my: 1,
                      "& .MuiSvgIcon-root": {
                        mr: "0.2em",
                      },
                    },
                  }}
                >
                  <Button variant="contained" component={Link} to="/login">
                    <SignInIcon />
                    CANCEL
                  </Button>

                  {(!respMsg || error) && (
                    <Button
                      variant="contained"
                      onClick={handleSubmit(onSubmit)}
                    >
                      <LockOpenIcon />
                      {error ? "RESEND" : "SEND"} ACCOUNT UNLOCK LINK
                    </Button>
                  )}
                </Grid>
              </CardActions>
              {respMsg && (
                <Typography
                  variant="subtitle1"
                  color={error ? "error" : "primary"}
                  sx={{ mb: "1em", textAlign: "center" }}
                >
                  {respMsg}
                </Typography>
              )}
            </form>
          </Card>
          <LocalLoader progress={isLoading} />
        </Grid>
      </Grid>
    </div>
  );
}
export default UnlockAccountPage;
